import { Injectable, Signal, signal } from '@angular/core';
import { initializeApp, FirebaseApp, getApps, getApp } from 'firebase/app';
import {
    getRemoteConfig,
    RemoteConfig,
    fetchAndActivate,
    getValue as firebaseGetValue,
    Value,
    isSupported
} from 'firebase/remote-config';
import { environment } from '../../../../environments/environment';

/**
 * Thin wrapper around Firebase Remote Config SDK.
 * No component or higher-level service imports from 'firebase/*' directly.
 *
 * Bootstrap is fire-and-forget: the constructor kicks off Firebase init without
 * blocking the Angular bootstrap. Consumers observe the `isReady` signal to know
 * when fetched values are available. Until then, getValue() returns null and
 * callers fall back to defaults — the app must not crash due to Firebase
 * unavailability or slow networks.
 */
@Injectable({ providedIn: 'root' })
export class RemoteConfigService {
    private remoteConfig: RemoteConfig | null = null;
    private readonly _isReady = signal(false);

    /** Flips to true once Firebase init has resolved (success OR failure). */
    readonly isReady: Signal<boolean> = this._isReady.asReadonly();

    constructor() {
         void this.bootstrap();
    }

    private async bootstrap(): Promise<void> {
        try {
            const supported = await isSupported();
            if (!supported) {
                console.warn('[RemoteConfig] Not supported in this environment. Using defaults.');
                return;
            }

            // Guard against duplicate initialization during HMR restarts
            const app: FirebaseApp = getApps().length === 0
                ? initializeApp(environment.firebase)
                : getApp();

            this.remoteConfig = getRemoteConfig(app);
            this.remoteConfig.settings = {
                minimumFetchIntervalMillis: environment.firebase.remoteConfigFetchIntervalSeconds * 1000,
                fetchTimeoutMillis: environment.firebase.remoteConfigTimeoutMillis
            };

            await fetchAndActivate(this.remoteConfig);
        } catch (error) {
            console.warn('[RemoteConfig] Initialization failed. All experiments will use default variants.', error);
        } finally {
            this._isReady.set(true);
        }
    }

    getValue(key: string): Value | null {
        if (!this.remoteConfig) {
            return null;
        }
        return firebaseGetValue(this.remoteConfig, key);
    }

    getStringValue(key: string): string {
        const value = this.getValue(key);
        return value ? value.asString() : '';
    }
}
